import { Box, Button, Chip, Grid, Paper, Typography, useTheme } from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";

import { hexToRgba } from "@aclymatepackages/converters";

import SlideLayout from "../../../../../layouts/SlideLayout";

import { matchBasisLabel } from "./helpers";

const ZeroTxnVendorRow = ({ vendor, label, chipColor }) => (
  <Paper variant="outlined">
    <Box p={2}>
      <Grid container alignItems="center" spacing={1} wrap="nowrap">
        <Grid item xs>
          <Typography variant="subtitle1">{vendor.name}</Typography>
          <Typography variant="caption" color="textSecondary">
            {`Industry: ${vendor.naicsTitle || "Unknown"}`}
          </Typography>
        </Grid>
        <Grid item>
          <Chip size="small" label={label} color={chipColor} variant="outlined" />
        </Grid>
      </Grid>
    </Box>
  </Paper>
);

// Deliberately not a variant of DuplicateClusterSlider -- there's no primary
// selection, no NAICS override, and no merge to configure here. The only
// decision is whether these two names really are the same vendor; confirming
// archives the empty side (see confirmZeroTxnCluster in useCleanupCandidates),
// it never touches transactions.
const ZeroTxnClusterSlider = ({ cluster, onClose, onConfirmArchive, onDismiss, isConfirming }) => {
  const { palette } = useTheme();
  const { vendors, matchBasis, reason, recommendedPrimaryId } = cluster;

  const keptVendor =
    vendors.find(({ id }) => id === recommendedPrimaryId) || vendors[0];
  const emptyVendor = vendors.find(({ id }) => id !== keptVendor.id) || vendors[1];

  const onConfirm = () => {
    onConfirmArchive(cluster);
    onClose();
  };

  // dismissCluster already builds a dismissal pair from every combination of
  // cluster.vendors -- with exactly two vendors here, that's this pair, so
  // no extra rejectedPairs are needed (unlike DuplicateClusterSlider, which
  // supports removing individual vendors from a larger cluster).
  const onConfirmDismiss = () => {
    onDismiss(cluster.clusterId);
    onClose();
  };

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={onClose}
      header={
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item>
            <Box
              p={1.5}
              style={{
                backgroundColor: hexToRgba(palette.primary.main, 0.15),
                borderRadius: 999,
                display: "flex"
              }}
            >
              <ArchiveIcon color="primary" />
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h5">Confirm empty-duplicate vendor</Typography>
            <Typography variant="caption" color="textSecondary">
              {matchBasisLabel(matchBasis)}
            </Typography>
          </Grid>
        </Grid>
      }
      content={
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              {reason} This vendor has no transactions, so nothing will be
              reassigned — confirming just archives the empty record below.
            </Typography>
          </Grid>
          <Grid item>
            <ZeroTxnVendorRow
              vendor={keptVendor}
              label="Kept — has transactions"
              chipColor="success"
            />
          </Grid>
          <Grid item>
            <ZeroTxnVendorRow
              vendor={emptyVendor}
              label="Will be archived — no transactions"
              chipColor="default"
            />
          </Grid>
        </Grid>
      }
      footer={
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <Grid item>
            <Button onClick={onConfirmDismiss} color="inherit">
              Not a duplicate — dismiss
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ArchiveIcon />}
              disabled={isConfirming}
              onClick={onConfirm}
            >
              {isConfirming
                ? "Archiving…"
                : `Confirm & archive "${emptyVendor.name}"`}
            </Button>
          </Grid>
        </Grid>
      }
    />
  );
};

export default ZeroTxnClusterSlider;
